/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Skeleton } from 'antd'
import {
  getCareerSummary,
  getTeamSeasonStats,
} from '@sportninja/common/features/playerFinder/aggregateSeasonStats'
import usePlayerSeasonStats from '@sportninja/common/features/profile/hooks/usePlayerSeasonStats'
import type { PlayerProfileResponse } from '@sportninja/common/services/player/playerProfile'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import StatTable from './StatTable'
import TeamStatsTable from './TeamStatsTable'
import {
  detailSectionPanelStyle,
  detailSectionStyle,
  detailSectionTitleStyle,
} from './detailSectionStyles'

interface PlayerTeam {
  id?: string
  image?: unknown
  sport_id?: string
  generic_sport_id?: string | null
}

export interface PlayerFinderStatsOverviewProps {
  player: PlayerProfileResponse
  /**
   * The game's sport id. Player Finder is game-scoped, so stats are shown for
   * this sport ONLY — no sport selector. Nothing renders until it resolves.
   */
  sportId?: string
}

/**
 * Read-only stats for the spare-candidate detail modal: a Career Stats summary
 * plus a per-team Statistics table, both scoped to the game's sport.
 *
 * Deliberately NOT the profile's `PlayerStatsBySeason` — that one carries a
 * sport selector, "View All" links and clickable rows that don't belong in this
 * read-only card, and it renders every sport rather than the game's. This
 * mirrors mobile's PlayerFinderStatsOverview and reuses the same shared
 * aggregation helpers, so the two platforms can't drift.
 */
const PlayerFinderStatsOverview: React.FC<PlayerFinderStatsOverviewProps> = ({
  player,
  sportId,
}) => {
  'use memo'
  // Find the player's team in the game's sport to learn whether it's a generic
  // sport (the stats fetch is keyed by both ids).
  const teams = (player.teams as PlayerTeam[] | undefined) ?? []
  const matchTeam = teams.find((t) => t.sport_id === sportId)
  const genericSportId = matchTeam?.generic_sport_id ?? null

  const { playerStats, goalieStats, isLoading } = usePlayerSeasonStats({
    playerId: player.id,
    sportId,
    genericSportId,
    isSkater: true,
    isGoalie: true,
    enabled: Boolean(player.id) && Boolean(sportId),
  })

  const rows = [...playerStats, ...goalieStats]
  const career = getCareerSummary(rows)
  const teamStats = getTeamSeasonStats(rows, teams)

  if (isLoading && rows.length === 0) {
    return <Skeleton active paragraph={{ rows: 4 }} />
  }

  if (rows.length === 0) {
    return null
  }

  return (
    <div css={containerStyle}>
      {career.length > 0 && (
        <div css={detailSectionStyle}>
          <span css={detailSectionTitleStyle}>
            {capitalizeWords('Career Stats')}
          </span>
          <div css={detailSectionPanelStyle}>
            <StatTable cells={career} />
          </div>
        </div>
      )}

      {teamStats.length > 0 && (
        <div css={detailSectionStyle}>
          <span css={detailSectionTitleStyle}>
            {capitalizeWords('Statistics')}
          </span>
          <TeamStatsTable teams={teamStats} />
        </div>
      )}
    </div>
  )
}

export default PlayerFinderStatsOverview

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
